import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from "react"
import type { AppDispatch } from "@core/store"
import { io, Socket } from "socket.io-client"
import { App as CapacitorApp } from "@capacitor/app"
import { Capacitor } from "@capacitor/core"
import { Network } from "@capacitor/network"
import { useDispatch, useSelector } from "react-redux"
import { getCurrentServerUrls } from "@core/utils/serverConfig"
import { handleReferenceUpdate } from "@core/utils/referenceDataUpdater"
import { isLoggedIn } from "@core/utils/tokenValidator"
import { isAuthStorageEventKey } from "@core/utils/webSessionAuth"
import { checkAndroidServerHealth } from "@modules/android/androidServerHealth"

import { selectedDateSelector } from "@/features/selectedDate/selectedDateSelectors"
import { fetchServerInfo } from "@/features/hospital/hospitalSlice"

import { waitingVisit, treatmentVisit, completeVisit } from "@/features/visit/visitSlice"
import { reservationFetchThunk } from "@core/api/reservationApi"
import { handleSioUpdate } from "@core/utils/sioDataUpdater"
import type { SocketState } from "./SocketContext.types"

const ANDROID_LINK_HEALTH_PING_INTERVAL_MS = 45_000
const ANDROID_LINK_HEALTH_RECONNECT_COOLDOWN_MS = 10_000

function createSocket(wsUrl: string | null): Socket | null {
  if (!wsUrl) return null

  const isCapacitorNative = Capacitor.isNativePlatform()

  if (isCapacitorNative) {
    return io(wsUrl, {
      path: "/socket.io",
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      transports: ["websocket", "polling"],
      upgrade: true,
    })
  }

  return io(wsUrl, {
    path: "/socket.io",
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    transports: ["websocket"],
    upgrade: false,
  })
}

const AndroidSocketContext = createContext<SocketState | null>(null)

function shouldUseAndroidLinkHealthPing() {
  return Capacitor.isNativePlatform() && __APP_VARIANT__ === "link"
}

async function probeServerReachable(timeoutMs = 2500): Promise<boolean> {
  return checkAndroidServerHealth(timeoutMs)
}

export const AndroidSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch()
  const selectedDate = useSelector(selectedDateSelector)
  const selectedDateRef = useRef(selectedDate)
  const [connected, setConnected] = useState(false)
  const [available, setAvailable] = useState(false)
  const [limitedMode, setLimitedMode] = useState(false)
  const [error, setError] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const authDisconnectRef = useRef(false)
  const appActiveRef = useRef(true)
  const healthPingInFlightRef = useRef(false)
  const healthPingFailureCountRef = useRef(0)
  const lastHealthReconnectAtRef = useRef(0)
  const [reconnectKey, setReconnectKey] = useState(0)
  const reconnectThrottleRef = useRef(0)

  const applyConnectedState = useCallback(() => {
    setConnected(true)
    setAvailable(true)
    setLimitedMode(false)
    setError(false)
  }, [])

  const applyLimitedModeState = useCallback(() => {
    setConnected(false)
    setAvailable(true)
    setLimitedMode(true)
    setError(false)
  }, [])

  const applyDisconnectedState = useCallback(() => {
    setConnected(false)
    setAvailable(false)
    setLimitedMode(false)
    setError(true)
  }, [])

  const applySignedOutState = useCallback(() => {
    setConnected(false)
    setAvailable(false)
    setLimitedMode(false)
    setError(false)
  }, [])

  const requestReconnect = useCallback((preserveAvailability = false) => {
    setConnected(false)
    setLimitedMode(false)
    if (!preserveAvailability) {
      setAvailable(false)
      setError(false)
    }
    setReconnectKey(prev => prev + 1)
  }, [])

  const reconnect = useCallback(() => {
    requestReconnect(false)
  }, [requestReconnect])

  useEffect(() => {
    selectedDateRef.current = selectedDate
  }, [selectedDate])

  const handleConnect = () => {
    authDisconnectRef.current = false
    healthPingFailureCountRef.current = 0
    applyConnectedState()

    void (async () => {
      try {
        const tasks = [dispatch(fetchServerInfo())]
        await Promise.allSettled(tasks)
      } catch (e) {
        console.warn("[AndroidSocketContext] 초기 서버 정보 로드 실패(격리 처리)", e)
      }
    })()
  }

  const handleDisconnect = (reason: string) => {
    if (reason === "io client disconnect") {
      authDisconnectRef.current = false
      return
    }

    console.debug("[AndroidSocketContext] 소켓 연결 끊김", {
      reason,
      socketId: socketRef.current?.id,
      appActive: appActiveRef.current,
      documentHidden: document.hidden,
    })

    if (Capacitor.isNativePlatform()) {
      void probeServerReachable().then(reachable => {
        if (reachable) {
          console.warn("[AndroidSocketContext] 소켓 연결 끊김(모바일) - API는 도달 가능, 제한 모드로 유지")
          applyLimitedModeState()
          return
        }

        applyDisconnectedState()
      })
      return
    }

    applyDisconnectedState()
  }

  const handleConnectError = async (connectError: any) => {
    console.error("[AndroidSocketContext] 소켓 연결 오류", {
      message: connectError?.message,
      appActive: appActiveRef.current,
      documentHidden: document.hidden,
      connectError,
    })

    if (Capacitor.isNativePlatform()) {
      const reachable = await probeServerReachable()
      if (reachable) {
        console.warn("[AndroidSocketContext] 소켓 실패(모바일) - API는 도달 가능, 제한 모드로 계속 진행")
        applyLimitedModeState()
        return
      }
    }

    applyDisconnectedState()
  }

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let appStateListener: { remove: () => Promise<void> } | null = null
    let networkListener: { remove: () => Promise<void> } | null = null

    const throttledReconnect = (reason: string) => {
      const now = Date.now()
      if (now - reconnectThrottleRef.current < 1500) {
        return
      }

      reconnectThrottleRef.current = now
      console.debug(`[AndroidSocketContext] ${reason} - 소켓 강제 재연결 요청`)
      requestReconnect(true)
    }

    const handleAppResume = async () => {
      appActiveRef.current = true

      try {
        const status = await Network.getStatus()
        if (!status.connected) {
          applyDisconnectedState()
          return
        }
      } catch {
        // noop
      }

      throttledReconnect("앱 복귀")
    }

    const initializeNativeListeners = async () => {
      appStateListener = await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        appActiveRef.current = isActive
        if (!isActive) return
        void handleAppResume()
      })

      networkListener = await Network.addListener("networkStatusChange", status => {
        console.debug("[AndroidSocketContext] 네트워크 상태 변경", status)

        if (!status.connected) {
          applyDisconnectedState()
          return
        }

        throttledReconnect("네트워크 전환")
      })
    }

    void initializeNativeListeners()

    return () => {
      void appStateListener?.remove()
      void networkListener?.remove()
    }
  }, [applyDisconnectedState, requestReconnect])

  useEffect(() => {
    if (!shouldUseAndroidLinkHealthPing()) return

    const runHealthPing = async () => {
      if (healthPingInFlightRef.current) return
      if (!appActiveRef.current || document.hidden) {
        healthPingFailureCountRef.current = 0
        return
      }
      if (!isLoggedIn()) {
        healthPingFailureCountRef.current = 0
        return
      }

      const socket = socketRef.current
      const socketConnectedBeforeCheck = Boolean(socket?.connected)
      const shouldProbeHealth = limitedMode || error || !socketConnectedBeforeCheck

      if (!shouldProbeHealth) {
        healthPingFailureCountRef.current = 0
        return
      }

      healthPingInFlightRef.current = true

      try {
        const reachable = await probeServerReachable()
        const currentSocket = socketRef.current
        const currentSocketConnected = Boolean(currentSocket?.connected)

        if (reachable) {
          healthPingFailureCountRef.current = 0

          if (currentSocket && !currentSocketConnected) {
            const now = Date.now()
            if (now - lastHealthReconnectAtRef.current >= ANDROID_LINK_HEALTH_RECONNECT_COOLDOWN_MS) {
              lastHealthReconnectAtRef.current = now
              console.debug("[AndroidSocketContext] 주기 health ping 성공 - 소켓 복구 시도", {
                limitedMode,
                error,
              })
              requestReconnect(true)
            }
          }
          return
        }

        if (!(limitedMode || error)) {
          healthPingFailureCountRef.current = 0
          return
        }

        healthPingFailureCountRef.current += 1

        if (healthPingFailureCountRef.current >= 2) {
          console.warn("[AndroidSocketContext] 주기 health ping 연속 실패 - 연결 오류 상태로 전환", {
            failureCount: healthPingFailureCountRef.current,
          })
          healthPingFailureCountRef.current = 0
          applyDisconnectedState()
        }
      } finally {
        healthPingInFlightRef.current = false
      }
    }

    void runHealthPing()

    const intervalId = window.setInterval(() => {
      void runHealthPing()
    }, ANDROID_LINK_HEALTH_PING_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [applyDisconnectedState, error, limitedMode, requestReconnect])

  useEffect(() => {
    const isReceptionOrConsentScreen = () => {
      const path = window.location.pathname + window.location.hash
      return path.includes("/android/reception/") || path.includes("/android/consent") || path.includes("/android/no-chart-consent")
    }

    const handleVisitUpdate = (data: string | { visitStatus: string }) => {
      if (!isLoggedIn()) {
        console.debug("[AndroidSocketContext] 로그인 전 방문 상태 브로드캐스트 무시")
        return
      }
      if (isReceptionOrConsentScreen()) {
        console.debug("[AndroidSocketContext] 접수/동의서 화면 — 방문 상태 브로드캐스트 무시")
        return
      }
      const currentSelectedDate = selectedDateRef.current
      const visitStatus = typeof data === "string" ? data : data.visitStatus
      console.log("방문 상태 업데이트 수신:", visitStatus)
      console.log("선택된 날짜:", currentSelectedDate)

      switch (visitStatus) {
        case "reservation":
          dispatch(reservationFetchThunk(currentSelectedDate))
          break
        case "waiting":
          dispatch(waitingVisit.fetchList(currentSelectedDate))
          break
        case "treatment":
          dispatch(treatmentVisit.fetchList(currentSelectedDate))
          break
        case "complete":
          dispatch(completeVisit.fetchList(currentSelectedDate))
          break
      }
    }

    const handleReferenceUpdateWrapper = async (data: { reference: string }) => {
      if (!isLoggedIn()) {
        console.debug("[AndroidSocketContext] 로그인 전 참조 브로드캐스트 무시")
        return
      }
      if (isReceptionOrConsentScreen()) {
        console.debug("[AndroidSocketContext] 접수/동의서 화면 — 참조 브로드캐스트 무시")
        return
      }
      await handleReferenceUpdate(data.reference)
    }

    const handleSioUpdateWrapper = async (data: any) => {
      if (!isLoggedIn()) {
        console.debug("[AndroidSocketContext] 로그인 전 액션 브로드캐스트 무시")
        return
      }
      if (isReceptionOrConsentScreen()) {
        console.debug("[AndroidSocketContext] 접수/동의서 화면 — 액션 브로드캐스트 무시")
        return
      }
      console.debug("액션 데이터 업데이트 수신:", data)
      await handleSioUpdate(data)
    }

    let cleanup: (() => void) | null = null

    const initializeSocket = async () => {
      try {
        const urls = getCurrentServerUrls()

        if (!urls.WS_BASE_URL) {
          console.warn("[AndroidSocketContext] 부트스트랩 서버 설정이 없습니다. 연결 오류 상태로 설정합니다.")
          applyDisconnectedState()
          return
        }

        const socket = createSocket(urls.WS_BASE_URL)
        if (!socket) {
          console.error("[AndroidSocketContext] 소켓 인스턴스 생성 실패")
          applyDisconnectedState()
          return
        }

        socketRef.current = socket

        socket.on("connect", handleConnect)
        socket.on("disconnect", handleDisconnect)
        socket.on("connect_error", handleConnectError)
        socket.on("visit_update", handleVisitUpdate)
        socket.on("reference_update", handleReferenceUpdateWrapper)
        socket.on("action_update", handleSioUpdateWrapper)

        if (isLoggedIn()) {
          socket.connect()
        } else {
          applySignedOutState()
        }

        cleanup = () => {
          if (socket) {
            socket.off("connect", handleConnect)
            socket.off("disconnect", handleDisconnect)
            socket.off("connect_error", handleConnectError)
            socket.off("visit_update", handleVisitUpdate)
            socket.off("reference_update", handleReferenceUpdateWrapper)
            socket.off("action_update", handleSioUpdateWrapper)
            socket.disconnect()
          }
        }
      } catch (initializeError) {
        console.error("[AndroidSocketContext] 소켓 초기화 중 오류", initializeError)
        applyDisconnectedState()
      }
    }

    initializeSocket()

    return () => {
      if (cleanup) cleanup()
    }
  }, [applyDisconnectedState, applySignedOutState, dispatch, reconnectKey])

  useEffect(() => {
    const handleAuthStorageChange = (event: Event) => {
      const storageEvent = event as StorageEvent

      if (typeof storageEvent.key !== "undefined" && !isAuthStorageEventKey(storageEvent.key)) {
        return
      }

      const socket = socketRef.current
      if (!socket) {
        return
      }

      if (!isLoggedIn()) {
        authDisconnectRef.current = true
        socket.disconnect()
        applySignedOutState()
        return
      }

      if (!socket.connected) {
        setError(false)
        socket.connect()
      }
    }

    window.addEventListener("storage", handleAuthStorageChange)

    return () => {
      window.removeEventListener("storage", handleAuthStorageChange)
    }
  }, [applySignedOutState])

  return (
    <AndroidSocketContext.Provider value={{ socket: socketRef.current, connected, available, limitedMode, error, reconnect }}>
      {children}
    </AndroidSocketContext.Provider>
  )
}

export const useAndroidSocket = (): SocketState => {
  const ctx = useContext(AndroidSocketContext)
  if (!ctx) {
    console.warn("AndroidSocketContext가 없습니다. 기본 연결 오류 상태를 반환합니다.")
    return {
      socket: null,
      connected: false,
      available: false,
      limitedMode: false,
      error: true,
      reconnect: () => {},
    }
  }
  return ctx
}

